import { RECORDS_CLOSE } from "./recordsNavigation.js";

/** A single records browser, independent of hub/floor scene lifetimes. The
 * game document and socket remain alive while public pages navigate inside it. */
export class RecordsPanel {
  private dialog: HTMLDialogElement;
  private frame: HTMLIFrameElement;
  private back: HTMLButtonElement;
  private restoreFocus: HTMLElement | null = null;

  constructor(private root: HTMLElement) {
    this.dialog = document.createElement("dialog");
    this.dialog.id = "records-panel";
    this.dialog.setAttribute("aria-label", "The boards");
    const toolbar = document.createElement("div");
    toolbar.className = "records-panel-toolbar";
    const title = document.createElement("strong");
    title.textContent = "The boards";
    const back = document.createElement("button");
    this.back = back;
    back.type = "button";
    back.textContent = "← Back to game";
    back.addEventListener("click", () => this.close());
    toolbar.append(title, back);
    this.frame = document.createElement("iframe");
    this.frame.title = "The boards, maps and replay";
    this.dialog.append(toolbar, this.frame);
    document.body.append(this.dialog);
    this.dialog.addEventListener("cancel", (event) => { event.preventDefault(); this.close(); });
    // Game keyboard listeners live on window; modal inertness alone does not
    // stop a bubbled key on the panel's chrome from reaching them.
    const guard = (event: KeyboardEvent): void => {
      if (this.dialog.open) event.stopImmediatePropagation();
    };
    window.addEventListener("keydown", guard, true);
    window.addEventListener("keyup", guard, true);
    window.addEventListener("message", (event: MessageEvent) => {
      if (event.origin === location.origin && event.source === this.frame.contentWindow && event.data === RECORDS_CLOSE) this.close();
    });
    root.addEventListener("click", (event) => {
      const link = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>("a[data-records]") : null;
      if (!link) return;
      event.preventDefault(); this.open(link.href);
    });
  }

  open(path: string): void {
    const url = new URL(path, location.origin);
    if (url.origin !== location.origin || !["/leaderboard", "/board", "/run"].includes(url.pathname)) return;
    // The game's boards button reopens where you left off — a book view, a
    // full board, a run. An explicit link still goes where it points.
    const entry = url.pathname === "/leaderboard" && !url.searchParams.has("view");
    const target = entry && this.lastPath ? new URL(this.lastPath, location.origin) : url;
    if (!["/leaderboard", "/board", "/run"].includes(target.pathname)) target.pathname = "/leaderboard";
    target.searchParams.set("embedded", "1");
    this.back.textContent = this.root.querySelector("#auth") ? "← Back to sign in" : "← Back to game";
    this.restoreFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.frame.src = target.pathname + target.search;
    if (!this.dialog.open) this.dialog.showModal();
  }

  /** Where the panel's own browsing ended, so the next opening resumes it.
   *  Read from the frame: same origin, and the page's own navigation is the
   *  state worth keeping (its query carries the board's filters). */
  private lastPath: string | null = null;

  close(): void {
    if (!this.dialog.open) return;
    try {
      const where = this.frame.contentWindow?.location;
      if (where && where.pathname !== "about:blank") this.lastPath = where.pathname + where.search;
    } catch { this.lastPath = null; } // A cross-origin frame is not ours to resume.
    this.dialog.close();
    this.frame.src = "about:blank"; // Stop playback and release the page's listeners.
    if (this.restoreFocus?.isConnected) this.restoreFocus.focus();
    this.restoreFocus = null;
  }
}
